import { Stack } from '@mui/material';
import { useCallback, useEffect, useMemo } from 'react';
import { useWatch } from 'react-hook-form';

import { Button } from '@/components/form/baseInputs/Button';
import { FormAwareSelectInput } from '@/components/form/formAwareInputs/FormAwareSelectInput';
import { FormModal } from '@/components/modals/FormModal/FormModal';
import {
  FormProvider,
  useForm,
  useFormContext,
} from '@/components/react-hook-form';
import { DOCUMENT_TYPE_DISPLAY_NAMES } from '@/modules/documents/documents.constants';
import { isClientProfileDocumentType } from '@/modules/documents/documents.utils';
import { DocumentType, PresentationDocumentType } from '@/types/schema';

import { usePresentationTemplateEditorContext } from '../../../PresentationTemplateEditor.context';
import {
  PresentationTemplateEditorBundle,
  PresentationTemplateEditorFormShape,
} from '../../../PresentationTemplateEditor.types';
import {
  buildDocumentSummaryBundleFromForm,
  CLIENT_SELECTION_OPTIONS,
  DocumentSummaryBundleConfigFormShape,
  getDefaultDocumentSummaryValues,
} from './DocumentSummaryBundleConfigModal.utils';

export interface DocumentSummaryBundleConfigModalProps {
  /** null for create mode, index for edit mode */
  bundleIndex: number | null;
  onClose: () => void;
}

/**
 * Maps PresentationDocumentType enum values
 * to their display names, reusing the existing DOCUMENT_TYPE_DISPLAY_NAMES
 * map since both enums share the same underlying string values.
 */
const DOCUMENT_TYPE_OPTIONS = Object.values(PresentationDocumentType)
  .map((value) => ({
    display:
      DOCUMENT_TYPE_DISPLAY_NAMES[value as unknown as DocumentType] ?? value,
    value,
  }))
  .sort((a, b) => {
    // "Other" always goes last
    if (a.value === PresentationDocumentType.Other) return 1;
    if (b.value === PresentationDocumentType.Other) return -1;
    return a.display.localeCompare(b.display);
  });

/**
 * Reads the parent form's bundle data (for edit mode), then renders the modal
 * with its own nested FormProvider to avoid polluting the parent form.
 */
export function DocumentSummaryBundleConfigModal({
  bundleIndex,
  onClose,
}: DocumentSummaryBundleConfigModalProps) {
  const { control } = useFormContext<PresentationTemplateEditorFormShape>();

  const existingBundle = useWatch({
    control,
    name:
      bundleIndex !== null
        ? (`bundles.${bundleIndex}` as const)
        : (undefined as never),
  }) as PresentationTemplateEditorBundle | undefined;

  const isEditMode = bundleIndex !== null;

  return (
    <DocumentSummaryBundleConfigModalInner
      existingBundle={isEditMode ? existingBundle : undefined}
      bundleIndex={bundleIndex}
      onClose={onClose}
    />
  );
}

interface DocumentSummaryBundleConfigModalInnerProps {
  existingBundle?: PresentationTemplateEditorBundle;
  bundleIndex: number | null;
  onClose: () => void;
}

function DocumentSummaryBundleConfigModalInner({
  existingBundle,
  bundleIndex,
  onClose,
}: DocumentSummaryBundleConfigModalInnerProps) {
  const { bundlesFormArray, markBundlesDirty } =
    usePresentationTemplateEditorContext();
  const isEditMode = bundleIndex !== null;

  const form = useForm<DocumentSummaryBundleConfigFormShape>({
    defaultValues: getDefaultDocumentSummaryValues(existingBundle),
    mode: 'onChange',
  });

  const { isValid } = form.formState;

  const documentType = useWatch({
    control: form.control,
    name: 'documentType',
  });
  const showClientDropdown = isClientProfileDocumentType(documentType);

  // Unregister the client selection field when hidden so its value is not
  // included in the form submission for non-client-profile document types.
  useEffect(() => {
    if (!showClientDropdown) {
      form.unregister('clientSelection');
    }
  }, [showClientDropdown, form]);

  const clientOptions = useMemo(
    () =>
      CLIENT_SELECTION_OPTIONS.map((opt) => ({
        display: opt.display,
        value: opt.value,
      })),
    []
  );

  const handleSave = useCallback(() => {
    const formValues = form.getValues();
    const bundle = buildDocumentSummaryBundleFromForm(
      formValues,
      existingBundle
    );

    if (isEditMode && bundleIndex !== null) {
      bundlesFormArray.update(bundleIndex, bundle);
      // useFieldArray.update() doesn't propagate dirty state to the parent
      // form, so flip isDirty explicitly to keep the Save button enabled.
      markBundlesDirty();
    } else {
      bundlesFormArray.append(bundle);
    }

    onClose();
  }, [
    form,
    existingBundle,
    isEditMode,
    bundleIndex,
    bundlesFormArray,
    markBundlesDirty,
    onClose,
  ]);

  const actions = useMemo(
    () => (
      <Stack direction="row" justifyContent="flex-end" spacing={2} width="100%">
        <Button variant="secondary" size="sm" onClick={onClose}>
          Cancel
        </Button>
        <Button
          variant="primary"
          size="sm"
          onClick={handleSave}
          disabled={!isValid}
        >
          {isEditMode ? 'Save' : 'Add document summaries'}
        </Button>
      </Stack>
    ),
    [onClose, handleSave, isValid, isEditMode]
  );

  return (
    <FormProvider {...form}>
      <FormModal
        heading={
          isEditMode ? 'Edit document summaries' : 'Add document summaries'
        }
        isOpen
        onClose={onClose}
        actions={actions}
        rightHeaderContent={null}
      >
        <Stack direction="column" spacing={2}>
          <FormAwareSelectInput<DocumentSummaryBundleConfigFormShape>
            control={form.control}
            fieldName="documentType"
            label="Document type"
            options={DOCUMENT_TYPE_OPTIONS}
            required
          />
          {showClientDropdown && (
            <FormAwareSelectInput<DocumentSummaryBundleConfigFormShape>
              control={form.control}
              fieldName="clientSelection"
              label="Associated individuals"
              options={clientOptions}
              required
            />
          )}
        </Stack>
      </FormModal>
    </FormProvider>
  );
}
